import { useCallback, useEffect, useRef, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { BudgetFilters, BudgetPageData } from './config';

interface UseBudgetDataResult {
  data: BudgetPageData | null;
  loading: boolean;
  /** True while a refetch is in flight but stale data is still on screen. */
  refreshing: boolean;
  error: string | null;
  refetch: () => void;
  setData: (updater: (prev: BudgetPageData | null) => BudgetPageData | null) => void;
}

export function useBudgetData(filters: BudgetFilters): UseBudgetDataResult {
  const supabase = useSupabaseClient();
  const [data, setData] = useState<BudgetPageData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);
  // Once we've successfully loaded once, every subsequent fetch is a "soft
  // refresh": keep the current data + grid on screen, just swap the rows in
  // when the new response lands. React keys keep unchanged rows mounted.
  const hasLoadedOnceRef = useRef<boolean>(false);

  const refetch = useCallback(() => setRefetchKey((k) => k + 1), []);

  const { fyStartYear, projectLead, clientId, search } = filters;
  const typesKey = filters.projectTypes.join(',');
  const statusesKey = filters.workflowStatuses.join(',');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const isInitial = !hasLoadedOnceRef.current;
      if (isInitial) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);

      const typesArr = typesKey ? typesKey.split(',') : [];
      const statusIds = statusesKey
        ? statusesKey.split(',').map((id) => parseInt(id, 10)).filter((n) => !isNaN(n))
        : [];
      const clientIdNum = clientId ? parseInt(clientId, 10) : NaN;

      const { data: rpcData, error: rpcError } = await supabase.rpc('get_budget_page_data', {
        p_fy_start_year: fyStartYear,
        p_types: typesArr.length > 0 ? typesArr : null,
        p_project_lead: projectLead || null,
        p_workflow_status_ids: statusIds.length > 0 ? statusIds : null,
        p_search: search || null,
        p_client_id: !isNaN(clientIdNum) ? clientIdNum : null,
      });

      if (cancelled) return;

      if (rpcError) {
        console.error('Error loading budget data:', rpcError);
        setError(rpcError.message);
        // Only blow away the grid on the initial fetch failure. On a soft
        // refresh keep the last-known-good data visible so the user isn't
        // staring at an empty screen because of a transient network blip.
        if (isInitial) setData(null);
      } else {
        setData(rpcData as BudgetPageData);
        hasLoadedOnceRef.current = true;
      }
      if (isInitial) setLoading(false);
      setRefreshing(false);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [supabase, fyStartYear, typesKey, projectLead, statusesKey, clientId, search, refetchKey]);

  return { data, loading, refreshing, error, refetch, setData };
}
